import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { userFacingError } from '../i18n/userFacingError';

// Hook para queries com React Query
export const useApiQuery = (key, apiCall, options = {}) => {
  return useQuery(key, apiCall, {
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403 || status === 404 || status === 422) return false;
      return failureCount < 2;
    },
    ...options,
  });
};

// Hook para mutations com React Query
export const useApiMutation = (apiCall, options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation(apiCall, {
    onSuccess: (data, variables, context) => {
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
      
      // Invalidar queries relacionadas
      if (options.invalidateQueries) {
        options.invalidateQueries.forEach(queryKey => {
          queryClient.invalidateQueries(queryKey);
        });
      }
      
      // Mostrar toast de sucesso
      if (options.successMessage) {
        toast.success(options.successMessage);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
      
      // Mostrar toast de erro
      const errorMessage = options.errorMessage || userFacingError(error, 'requestError');
      toast.error(errorMessage);
    },
    ...options,
  });
};

// Hook para paginação
export const usePagination = (initialPage = 1, initialLimit = 10) => {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [total, setTotal] = useState(0);
  
  const totalPages = Math.ceil(total / limit);
  
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };
  
  const nextPage = () => goToPage(page + 1);
  const prevPage = () => goToPage(page - 1);
  
  const resetPagination = () => {
    setPage(1);
  };
  
  return {
    page,
    limit,
    total,
    totalPages,
    setPage,
    setLimit,
    setTotal,
    goToPage,
    nextPage,
    prevPage,
    resetPagination,
    hasNext: page < totalPages,
    hasPrev: page > 1,
  };
};

// Hook para filtros
export const useFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [isFiltered, setIsFiltered] = useState(false);
  
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setIsFiltered(true);
  };
  
  const removeFilter = (key) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  };
  
  const clearFilters = () => {
    setFilters(initialFilters);
    setIsFiltered(false);
  };
  
  const hasActiveFilters = () => {
    return Object.values(filters).some(value => 
      value !== null && value !== undefined && value !== ''
    );
  };
  
  return {
    filters,
    isFiltered,
    updateFilter,
    removeFilter,
    clearFilters,
    hasActiveFilters: hasActiveFilters(),
  };
};

// Hook para busca
export const useSearch = (initialQuery = '') => {
  const [query, setQuery] = useState(initialQuery);
  const [isSearching, setIsSearching] = useState(false);
  
  const search = (newQuery) => {
    setQuery(newQuery);
    setIsSearching(newQuery.length > 0);
  };
  
  const clearSearch = () => {
    setQuery('');
    setIsSearching(false);
  };
  
  return {
    query,
    isSearching,
    search,
    clearSearch,
  };
};

// Hook para modal
export const useModal = (initialOpen = false) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [data, setData] = useState(null);
  
  const open = (modalData = null) => {
    setData(modalData);
    setIsOpen(true);
  };
  
  const close = () => {
    setIsOpen(false);
    setData(null);
  };
  
  return {
    isOpen,
    data,
    open,
    close,
  };
};

// Hook para loading states
export const useLoading = (initialLoading = false) => {
  const [loading, setLoading] = useState(initialLoading);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  const startLoading = (message = i18n.t('loading')) => {
    setLoading(true);
    setLoadingMessage(message);
  };
  
  const stopLoading = () => {
    setLoading(false);
    setLoadingMessage('');
  };
  
  return {
    loading,
    loadingMessage,
    startLoading,
    stopLoading,
  };
};

// Hook para notificações
export const useNotifications = () => {
  const showSuccess = (message) => {
    toast.success(message);
  };
  
  const showError = (message) => {
    toast.error(message);
  };
  
  const showWarning = (message) => {
    toast(message, {
      icon: '⚠️',
      style: {
        background: '#FF9800',
        color: '#fff',
      },
    });
  };
  
  const showInfo = (message) => {
    toast(message, {
      icon: 'ℹ️',
      style: {
        background: '#2196F3',
        color: '#fff',
      },
    });
  };
  
  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

// Hook para dados do dashboard
export const useDashboardData = (period = 'month') => {
  const { data: stats, isLoading: statsLoading, isError: statsError, error: statsErr } = useApiQuery(
    ['dashboard-stats', period],
    () =>
      import('../services/api').then((api) =>
        api.dashboardService.getStats(period).then((r) => r.data)
      )
  );

  const { data: licensesByStatus, isLoading: statusLoading, isError: statusError } = useApiQuery(
    'dashboard-licenses-by-status',
    () =>
      import('../services/api').then((api) =>
        api.dashboardService.getLicensesByStatus().then((r) => r.data)
      )
  );

  const { data: licensesTrend, isLoading: trendLoading, isError: trendError } = useApiQuery(
    ['dashboard-licenses-trend', period],
    () =>
      import('../services/api').then((api) =>
        api.dashboardService.getLicensesTrend(period).then((r) => r.data)
      )
  );

  const { data: insights, isLoading: insightsLoading, isError: insightsError } = useApiQuery(
    ['dashboard-insights', period],
    () =>
      import('../services/api').then((api) =>
        api.dashboardService.getInsights(period).then((r) => r.data)
      )
  );

  const { data: recentActivity, isLoading: activityLoading, isError: activityError } = useApiQuery(
    'dashboard-recent-activity',
    () =>
      import('../services/api').then((api) =>
        api.dashboardService.getRecentActivity().then((r) => r.data)
      )
  );
  
  const isLoading = statsLoading || statusLoading || trendLoading || activityLoading || insightsLoading;
  const isError = statsError || statusError || trendError || activityError || insightsError;

  return {
    stats,
    licensesByStatus,
    licensesTrend,
    insights,
    recentActivity,
    isLoading,
    isError,
    error: statsErr,
  };
};

// Hook para CRUD operations
export const useCrud = (entityName, apiService) => {
  
  // Sem skip/limit/page: busca todas as páginas (APIs FastAPI limitam em 100).
  const useList = (params = {}) => {
    const { skip, limit, page, ...filters } = params || {};
    const hasPaging = skip != null || limit != null || page != null;
    return useApiQuery(
      [entityName, 'list', params],
      async () => {
        if (hasPaging) {
          return apiService.getList(params);
        }
        const { fetchAllFromGetList } = await import('../utils/apiPagination');
        return fetchAllFromGetList(apiService.getList, filters);
      }
    );
  };
  
  // Item específico
  const useItem = (id) => {
    return useApiQuery(
      [entityName, 'item', id],
      () => apiService.getItem(id),
      {
        enabled: !!id,
      }
    );
  };
  
  // Criar
  const useCreate = () => {
    return useApiMutation(
      (data) => apiService.create(data),
      {
        successMessage: i18n.t('createdSuccess'),
        invalidateQueries: [[entityName, 'list']],
      }
    );
  };
  
  // Atualizar
  const useUpdate = () => {
    return useApiMutation(
      ({ id, data }) => apiService.update(id, data),
      {
        successMessage: i18n.t('updatedSuccess'),
        invalidateQueries: [[entityName, 'list'], [entityName, 'item']],
      }
    );
  };
  
  // Deletar
  const useDelete = () => {
    return useApiMutation(
      (id) => apiService.delete(id),
      {
        successMessage: i18n.t('deletedSuccess'),
        invalidateQueries: [[entityName, 'list']],
      }
    );
  };
  
  return {
    useList,
    useItem,
    useCreate,
    useUpdate,
    useDelete,
  };
};
